import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { TypedMemo } from '../../common/types';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { ErrorViewProps } from '../../common/views/ErrorView/props';
import { artistHomeMessages as messages } from './artistHomeMessages';
import { Panel, PanelBody, PanelHead } from '../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Home; each part uses its own slice of it */
import styles from './ArtistHome.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface HomeBlockProps {
  icon: IconDefinition;
  title: string;
  /** "Open Catalog". Left out on a block that shows its setup card. */
  moreLabel?: string;
  onMore?: () => void;
  /** Set when the block's reads failed. The block then shows the standard ErrorView only. */
  error?: ErrorViewProps['error'];
  retry?: () => void;
  children: React.ReactNode;
}

/** The frame of a Home block: icon, title, "Open …" link, and its own error state (design a1, b3). */
const HomeBlockComponent: React.FC<HomeBlockProps> = ({ icon, title, moreLabel, onMore, error, retry, children }) => {
  const intl = useIntl();
  return (
    <Panel as="section" className={styles.block}>
      <PanelHead
        className={styles.blockHead}
        titleAs="h2"
        icon={<FontAwesomeIcon icon={icon} />}
        title={title}
        right={
          moreLabel !== undefined && onMore !== undefined && error === undefined ? (
            <button type="button" className={`${styles.linkButton} ${styles.blockMore}`} onClick={onMore}>
              {moreLabel}
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          ) : undefined
        }
      />
      <PanelBody stack>
        {error !== undefined ? (
          <>
            <ErrorView error={error} retry={retry} />
            <p className={styles.blockError}>{intl.formatMessage(messages.blockError)}</p>
          </>
        ) : (
          children
        )}
      </PanelBody>
    </Panel>
  );
};

export const HomeBlock = TypedMemo(HomeBlockComponent);
